'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

const navItems = [
  { href: '/products', key: 'products' },
  { href: '/cases', key: 'cases' },
  { href: '/pricing', key: 'pricing' },
  { href: '/blog', key: 'blog' },
  { href: '/about', key: 'about' },
  { href: '/contacts', key: 'contacts' },
] as const;

export function Header() {
  const t = useTranslations('nav');
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-surface/90 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* The real lockup, in the brand navy — the one place on a light
              surface where that hue is licensed. */}
          <Link href="/" aria-label="HIVE" className="flex items-center">
            <Logo className="h-6 w-auto text-brand" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                href={item.href}
                className={`px-3 py-2 text-body-sm font-medium rounded-lg transition-colors ${
                  pathname.startsWith(item.href)
                    ? 'text-ink'
                    : 'text-slate hover:text-ink'
                }`}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button href="/contacts" size="sm" className="max-md:hidden">
              {t('requestDemo')}
            </Button>

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden rounded-lg p-2 text-slate hover:text-ink"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-line">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  className={`px-3 py-2 text-body-sm font-medium rounded-lg transition-colors ${
                    pathname.startsWith(item.href)
                      ? 'text-ink'
                      : 'text-slate hover:text-ink'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t(item.key)}
                </Link>
              ))}
              <div className="mt-3 px-3">
                <Button href="/contacts" size="sm" className="w-full">
                  {t('requestDemo')}
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
